import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, FileText, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CreditNotes() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [newNote, setNewNote] = useState({
    customer_id: '', credit_date: new Date().toISOString().split('T')[0], reason: '',
    items: [{ description: 'Credit adjustment', quantity: 1, unit_price: 0 }]
  });

  useEffect(() => { fetchData(); }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [notesRes, custRes] = await Promise.all([
        fetch(`${API}/api/sales/credit-notes`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/api/sales/customers`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (notesRes.ok) setNotes((await notesRes.json()).credit_notes || []);
      if (custRes.ok) {
        const data = await custRes.json();
        setCustomers([...(data.customers || []), ...(data.sub_customers || []).map(s => ({ ...s, customer_id: s.sub_customer_id }))]);
      }
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const createNote = async () => {
    if (!newNote.customer_id || !newNote.reason || !newNote.items[0].unit_price) {
      toast.error('Please fill required fields'); return;
    }
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/sales/credit-notes`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newNote)
      });
      if (res.ok) { toast.success('Credit note created'); setShowNew(false); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (error) { toast.error('Failed'); }
    finally { setProcessing(false); }
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amt || 0);

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-6 w-6 animate-spin" /></div>;

  return (
    <Card className="mt-4">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" />Credit Notes</CardTitle>
          <Dialog open={showNew} onOpenChange={setShowNew}>
            <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />New Credit Note</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Create Credit Note</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div><Label>Customer *</Label>
                  <Select value={newNote.customer_id} onValueChange={(v) => setNewNote({ ...newNote, customer_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>{customers.map(c => <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name || c.contact_person}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Date</Label><Input type="date" value={newNote.credit_date} onChange={(e) => setNewNote({ ...newNote, credit_date: e.target.value })} /></div>
                <div><Label>Reason *</Label><Textarea value={newNote.reason} onChange={(e) => setNewNote({ ...newNote, reason: e.target.value })} rows={2} placeholder="Reason for credit note" /></div>
                <div className="border rounded p-3 bg-slate-50">
                  <Label className="text-sm font-medium">Credit Amount</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div><Label className="text-xs">Description</Label><Input value={newNote.items[0].description} onChange={(e) => setNewNote({ ...newNote, items: [{ ...newNote.items[0], description: e.target.value }] })} /></div>
                    <div><Label className="text-xs">Amount *</Label><Input type="number" min="0" value={newNote.items[0].unit_price} onChange={(e) => setNewNote({ ...newNote, items: [{ ...newNote.items[0], unit_price: parseFloat(e.target.value) || 0 }] })} /></div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
                <Button onClick={createNote} disabled={processing}>{processing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Credit Note #</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {notes.map((n) => (
              <TableRow key={n.credit_note_id}>
                <TableCell className="font-medium">{n.credit_number}</TableCell>
                <TableCell>{n.credit_date}</TableCell>
                <TableCell>{n.customer_name}</TableCell>
                <TableCell className="max-w-xs truncate">{n.reason}</TableCell>
                <TableCell className="text-right text-red-600">{formatCurrency(n.total_amount)}</TableCell>
                <TableCell><Badge variant={n.status === 'applied' ? 'default' : 'outline'}>{n.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {notes.length === 0 && <div className="text-center py-8 text-slate-500">No credit notes</div>}
      </CardContent>
    </Card>
  );
}
